import type { JourneyMode } from "@/data/schema/compiled";
import { WHOLE_BOOK_SPAN_ID } from "@/data/join-chapters";
import { formatChapterId } from "../../book/ChapterId";

/**
 * Short options on a player address, such as `?view=story&chapters=4-5&at=90`.
 * To add one: read it here, and apply it in apply.ts.
 */
export interface UrlOptions {
  journey?: JourneyMode;
  spanId?: string;
  atMs?: number;
  cameraLock?: boolean;
  autoZoom?: boolean;
}

function chaptersSpan(value: string | null): string | undefined {
  if (value === "all") {
    return WHOLE_BOOK_SPAN_ID;
  }
  const match = /^([1-9]\d*)(?:-([1-9]\d*))?$/.exec(value ?? "");
  if (match === null) {
    return undefined;
  }
  const first = Number(match[1]);
  const last = match[2] === undefined ? first : Number(match[2]);
  if (last < first) {
    return undefined;
  }
  return first === last
    ? formatChapterId(first)
    : `${formatChapterId(first)}..${formatChapterId(last)}`;
}

function onOff(value: string | null): boolean | undefined {
  if (value === "on") {
    return true;
  }
  return value === "off" ? false : undefined;
}

/** Anything unknown or malformed is ignored, so an old or mistyped link still opens. */
export function parseUrlOptions(search: string): UrlOptions {
  const params = new URLSearchParams(search);
  const view = params.get("view");
  const at = Number(params.get("at"));
  const options: UrlOptions = {};
  if (view === "story") {
    options.journey = "universe";
  } else if (view === "book") {
    options.journey = "book";
  }
  const spanId = chaptersSpan(params.get("chapters"));
  if (spanId !== undefined) {
    options.spanId = spanId;
  }
  if (params.has("at") && Number.isFinite(at) && at >= 0) {
    options.atMs = at * 1_000;
  }
  const cameraLock = onOff(params.get("lock"));
  if (cameraLock !== undefined) {
    options.cameraLock = cameraLock;
  }
  const autoZoom = onOff(params.get("zoom"));
  if (autoZoom !== undefined) {
    options.autoZoom = autoZoom;
  }
  return options;
}
